import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { FunctionItem3D } from '../../types/math';
import { RotateCcw, Play, Pause, Eye, Grid3X3, Palette } from 'lucide-react';

interface Plot3DProps {
  item: FunctionItem3D;
  customParams?: Record<string, number>;
  interactive?: boolean;
  className?: string;
}

type ColorTheme = 'viridis' | 'cyberpunk' | 'plasma';

export const Plot3D: React.FC<Plot3DProps> = ({
  item,
  customParams,
  interactive = true,
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [autoRotate, setAutoRotate] = useState(true);
  const [wireframe, setWireframe] = useState(false);
  const [colorTheme, setColorTheme] = useState<ColorTheme>('cyberpunk');

  const controlsRef = useRef<OrbitControls | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);
  const animFrameIdRef = useRef<number | null>(null);

  // Colormap functions
  const getColorForNormalizedVal = (t: number, theme: ColorTheme): THREE.Color => {
    const clamped = Math.max(0, Math.min(1, t));
    const color = new THREE.Color();

    if (theme === 'viridis') {
      // Deep indigo -> Teal -> Yellow
      if (clamped < 0.5) {
        const u = clamped / 0.5;
        color.setRGB(0.27 + 0.1 * u, 0.0 + 0.6 * u, 0.33 + 0.3 * u);
      } else {
        const u = (clamped - 0.5) / 0.5;
        color.setRGB(0.37 + 0.6 * u, 0.6 + 0.35 * u, 0.63 - 0.5 * u);
      }
    } else if (theme === 'cyberpunk') {
      // Hot magenta -> Purple -> Electric Cyan
      if (clamped < 0.5) {
        const u = clamped / 0.5;
        color.setRGB(0.95 - 0.4 * u, 0.1 + 0.2 * u, 0.6 + 0.3 * u);
      } else {
        const u = (clamped - 0.5) / 0.5;
        color.setRGB(0.55 - 0.45 * u, 0.3 + 0.6 * u, 0.9 + 0.1 * u);
      }
    } else {
      // Plasma: Blue -> Magenta -> Yellow
      if (clamped < 0.5) {
        const u = clamped / 0.5;
        color.setRGB(0.05 + 0.7 * u, 0.05 + 0.1 * u, 0.5 + 0.4 * u);
      } else {
        const u = (clamped - 0.5) / 0.5;
        color.setRGB(0.75 + 0.25 * u, 0.15 + 0.75 * u, 0.9 - 0.8 * u);
      }
    }
    return color;
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // 1. Scene & Camera
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x070d19);

    const width = container.clientWidth || 500;
    const height = container.clientHeight || 400;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(7, 6, 8);
    cameraRef.current = camera;

    // 2. Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;

    // Clear previous children
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }
    container.appendChild(renderer.domElement);

    // 3. OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 1.2;
    controls.maxDistance = 35;
    controls.minDistance = 2;
    controlsRef.current = controls;

    // 4. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);

    const dirLight1 = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight1.position.set(10, 20, 15);
    scene.add(dirLight1);

    const dirLight2 = new THREE.DirectionalLight(0x38bdf8, 0.6);
    dirLight2.position.set(-10, -10, -10);
    scene.add(dirLight2);

    // 5. Subtle Coordinate Grid Helper
    const gridHelper = new THREE.GridHelper(10, 10, 0x334155, 0x1e293b);
    gridHelper.position.y = -2.5;
    scene.add(gridHelper);

    const axesHelper = new THREE.AxesHelper(2.5);
    (axesHelper.material as THREE.Material).transparent = true;
    (axesHelper.material as THREE.Material).opacity = 0.6;
    scene.add(axesHelper);

    // 6. Surface Geometry Generation
    const geometry = new THREE.BufferGeometry();
    const uSegments = 60;
    const vSegments = 60;

    const positions: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];

    const uMin = item.uDomain[0];
    const uMax = item.uDomain[1];
    const vMin = item.vDomain[0];
    const vMax = item.vDomain[1];

    let minZ = Infinity;
    let maxZ = -Infinity;
    const tempVertices: [number, number, number][] = [];

    // Sample vertices
    for (let i = 0; i <= uSegments; i++) {
      const u = uMin + (i / uSegments) * (uMax - uMin);
      for (let j = 0; j <= vSegments; j++) {
        const v = vMin + (j / vSegments) * (vMax - vMin);

        let x = 0;
        let y = 0;
        let z = 0;

        if (item.type === 'explicit' && item.evaluateExplicit) {
          x = u;
          y = v;
          const rawZ = item.evaluateExplicit(u, v, customParams);
          z = isNaN(rawZ) || !isFinite(rawZ) ? 0 : rawZ;
          // Clamp extreme spikes
          z = Math.max(-10, Math.min(10, z));
        } else if (item.type === 'parametric' && item.evaluateParametric) {
          const pt = item.evaluateParametric(u, v, customParams);
          x = pt[0];
          y = pt[1];
          z = pt[2];
        }

        // Standard Three.js coordinates: Y is up, so swap Y and Z for explicit surfaces
        const threeX = x;
        const threeY = item.type === 'explicit' ? z * 0.6 : z;
        const threeZ = item.type === 'explicit' ? y : y;

        tempVertices.push([threeX, threeY, threeZ]);
        if (threeY < minZ) minZ = threeY;
        if (threeY > maxZ) maxZ = threeY;
      }
    }

    const zSpan = maxZ - minZ || 1;

    // Fill positions and colors
    for (const [vx, vy, vz] of tempVertices) {
      positions.push(vx, vy, vz);
      const normalizedHeight = (vy - minZ) / zSpan;
      const c = getColorForNormalizedVal(normalizedHeight, colorTheme);
      colors.push(c.r, c.g, c.b);
    }

    // Triangulate
    for (let i = 0; i < uSegments; i++) {
      for (let j = 0; j < vSegments; j++) {
        const a = i * (vSegments + 1) + j;
        const b = (i + 1) * (vSegments + 1) + j;
        const c = (i + 1) * (vSegments + 1) + (j + 1);
        const d = i * (vSegments + 1) + (j + 1);

        indices.push(a, b, d);
        indices.push(b, c, d);
      }
    }

    geometry.setIndex(indices);
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.computeVertexNormals();

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      side: THREE.DoubleSide,
      wireframe: wireframe,
      roughness: 0.35,
      metalness: 0.15
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);
    meshRef.current = mesh;

    // 7. Render Loop
    const animate = () => {
      animFrameIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Resize observer
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        if (w > 0 && h > 0) {
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        }
      }
    });
    resizeObserver.observe(container);

    return () => {
      if (animFrameIdRef.current) cancelAnimationFrame(animFrameIdRef.current);
      resizeObserver.disconnect();
      controls.dispose();
      renderer.dispose();
      geometry.dispose();
      material.dispose();
    };
  }, [item, customParams, wireframe, colorTheme]);

  // Update auto-rotate in controls
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.autoRotate = autoRotate;
    }
  }, [autoRotate]);

  const resetCamera = () => {
    if (cameraRef.current && controlsRef.current) {
      cameraRef.current.position.set(7, 6, 8);
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  };

  const cycleTheme = () => {
    const themes: ColorTheme[] = ['cyberpunk', 'viridis', 'plasma'];
    const nextIdx = (themes.indexOf(colorTheme) + 1) % themes.length;
    setColorTheme(themes[nextIdx]);
  };

  return (
    <div className={`relative w-full h-full flex flex-col rounded-xl overflow-hidden border border-slate-800 bg-[#070d19] ${className}`}>
      <div ref={containerRef} className="w-full h-full cursor-grab active:cursor-grabbing" />

      {/* Floating 3D Controls */}
      {interactive && (
        <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-slate-900/80 backdrop-blur-md px-2 py-1.5 rounded-lg border border-slate-700/60 shadow-lg text-slate-300">
          <button
            onClick={() => setAutoRotate(!autoRotate)}
            title={autoRotate ? 'Pause Rotation' : 'Auto-Rotate'}
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            {autoRotate ? <Pause className="w-4 h-4 text-teal-400" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setWireframe(!wireframe)}
            title={wireframe ? 'Solid Surface' : 'Wireframe Grid'}
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            {wireframe ? <Eye className="w-4 h-4 text-teal-400" /> : <Grid3X3 className="w-4 h-4" />}
          </button>
          <button
            onClick={cycleTheme}
            title={`Palette: ${colorTheme}`}
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            <Palette className="w-4 h-4 text-purple-400" />
          </button>
          <button
            onClick={resetCamera}
            title="Reset Camera"
            className="p-1 hover:bg-slate-800 hover:text-teal-300 rounded transition"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
          <div className="w-[1px] h-4 bg-slate-700 mx-0.5" />
          <span className="text-[10px] font-mono text-purple-400 font-semibold px-1">
            3D WebGL
          </span>
        </div>
      )}

      {/* Touch/mouse tip */}
      <div className="absolute bottom-2.5 left-3 pointer-events-none text-[11px] font-mono text-slate-500 bg-slate-950/60 px-2 py-0.5 rounded backdrop-blur-sm">
        Drag to orbit · Scroll to zoom
      </div>
    </div>
  );
};
